import React, { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  GraduationCap, Brain, Activity, Smile, Footprints, Hand, Users, Send, ArrowRight,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SECTORS = [
  {
    slug: "colegios",
    icon: GraduationCap,
    title: "Colegios y orientadores",
    short: "El 73 % de los niños con dificultades de aprendizaje tienen problemas visuales no detectados.",
    cases: [
      "Pierde el hilo y relee, salta líneas",
      "Letra irregular, dificultad para copiar de pizarra",
      "Inteligente pero bajo rendimiento, fatiga al estudiar",
      "Diagnósticos previos de TDAH o dislexia sin respuesta",
    ],
    pitch: "Ofrecemos a los colegios tres acciones gratuitas y sin compromiso: charla para familias, sesión formativa para profesores y orientadores, y un canal de derivación directa con respuesta rápida e informe detallado.",
  },
  {
    slug: "psicologos",
    icon: Brain,
    title: "Psicólogos y psicopedagogos",
    short: "Muchos pacientes con TDAH, dislexia o bajo rendimiento tienen un problema visual sin diagnosticar.",
    cases: [
      "Déficit de atención sin respuesta al tratamiento",
      "Dislexia o dificultades lectoras",
      "Bajo rendimiento con buena capacidad cognitiva",
      "Fatiga cognitiva, frustración escolar",
      "Problemas de memoria visual",
    ],
    pitch: "Aportamos al equipo la evaluación completa de las habilidades visuales: movimientos oculares, coordinación binocular, acomodación, percepción, integración visuo-motora y reflejos primitivos. No sustituimos vuestro trabajo, lo complementamos.",
  },
  {
    slug: "fisios",
    icon: Activity,
    title: "Fisioterapeutas y osteópatas",
    short: "Cuando dolor cervical, mareos o vértigo no remiten, el origen puede estar en el sistema visual.",
    cases: [
      "Síndrome de Deficiencia Postural (SDP)",
      "Dolor crónico de cabeza, cuello, hombros o espalda sin causa estructural",
      "Mareos, vértigo, sensación de inestabilidad",
      "Mala postura al trabajar en cerca",
      "Movimientos de cabeza al leer en lugar de mover los ojos",
    ],
    pitch: "Evaluamos el SDP y, cuando procede, prescribimos prismas posturales: lentes que actúan a nivel neurológico modificando las referencias espaciales del paciente. Compartimos informe contigo para coordinar el tratamiento.",
  },
  {
    slug: "dentistas",
    icon: Smile,
    title: "Dentistas y ortodoncistas",
    short: "Bruxismo, ATM y postura craneal pueden tener un componente visual silencioso.",
    cases: [
      "Bruxismo nocturno persistente",
      "Disfunción de la articulación temporomandibular (ATM)",
      "Posturas craneales compensatorias y laterodesviaciones",
      "Tensión cervical asociada al esfuerzo visual",
    ],
    pitch: "Trabajamos con vosotros cuando un componente postural ocular puede estar implicado. Evaluación visual coordinada y, si aplica, prescripción de prismas posturales. Informe compartido.",
  },
  {
    slug: "podologos",
    icon: Footprints,
    title: "Podólogos",
    short: "Pies y ojos son captores propioceptivos: cuando uno descompensa, el otro lo refleja.",
    cases: [
      "Pacientes con SDP que no responden solo a plantillas",
      "Descompensaciones laterales recurrentes",
      "Caídas inexplicables, torceduras de tobillo frecuentes",
      "Posturas asimétricas en bipedestación",
    ],
    pitch: "Cuando el captor pies se trabaja sin abordar el captor ojos, los resultados son parciales. Evaluación visual y propioceptiva, prescripción de prismas posturales y coordinación con tu plan de plantillas.",
  },
  {
    slug: "terapeutas_ocupacionales",
    icon: Hand,
    title: "Terapeutas ocupacionales",
    short: "Detrás de muchas dificultades de motricidad fina o integración sensorial hay habilidades visuales en juego.",
    cases: [
      "Torpeza motriz fina con componente visual",
      "Dificultad en tareas de coordinación ojo-mano",
      "Hipersensibilidad visual o auditiva",
      "Problemas de integración sensorial",
      "Daño cerebral · neurorrehabilitación",
    ],
    pitch: "Aportamos la evaluación visuo-motora y, si procede, terapia visual o lentes funcionales que apoyen tu trabajo. Especializados también en pacientes con daño cerebral adquirido.",
  },
  {
    slug: "multidisciplinares",
    icon: Users,
    title: "Centros multidisciplinares",
    short: "Construimos red. Os derivamos pacientes y vosotros a nosotros.",
    cases: [
      "Protocolos de derivación bidireccional",
      "Sesiones de presentación cruzadas",
      "Casos compartidos con seguimiento conjunto",
      "Acceso prioritario a nuestra agenda",
    ],
    pitch: "Si dirigís un centro con varios profesionales, podemos formalizar un protocolo de colaboración: derivación rápida, informe compartido, presencia en el equipo cuando lo necesitéis. La colaboración es bidireccional.",
  },
];

const STATS = [
  { value: "73 %", label: "de los niños con dificultades de aprendizaje presentan problemas visuales" },
  { value: "4 / 10", label: "niños no están visualmente preparados para las tareas escolares" },
  { value: "75-90 %", label: "de la información que reciben en clase entra por la ruta visual" },
];

const ProfessionalsPage = () => {
  const [activeSlug, setActiveSlug] = useState(null);
  const [form, setForm] = useState({
    professional_name: "",
    professional_role: "",
    professional_center: "",
    professional_email: "",
    professional_phone: "",
    patient_name: "",
    patient_age: "",
    patient_phone: "",
    reason: "",
    consent_data_protection: false,
    website: "",
  });
  const [sending, setSending] = useState(false);

  const openForm = (slug) => {
    setForm((f) => ({ ...f, professional_role: slug }));
    setActiveSlug(slug);
    document.getElementById("derivacion")?.scrollIntoView({ behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent_data_protection) {
      toast.error("Debe aceptar la política de protección de datos.");
      return;
    }
    if (!form.professional_role) {
      toast.error("Selecciona tu profesión.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/professional-referral`, form);
      toast.success("¡Recibido! Te contactamos en breve.");
      setForm({
        professional_name: "", professional_role: "", professional_center: "",
        professional_email: "", professional_phone: "", patient_name: "",
        patient_age: "", patient_phone: "", reason: "", consent_data_protection: false, website: "",
      });
      setActiveSlug(null);
    } catch {
      toast.error("No se pudo enviar. Inténtalo de nuevo o llámanos.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="professionals-page">
      {/* Hero */}
      <section className="section" style={{ paddingTop: "5rem", paddingBottom: "2.5rem" }}>
        <div className="max-w-5xl mx-auto px-6">
          <span className="section-eyebrow">Para profesionales</span>
          <h1 className="section-title mt-4" style={{ fontSize: "clamp(2.2rem, 4.5vw, 3.4rem)", lineHeight: 1.05 }}>
            Juntos vemos lo que <span className="handwritten">ninguno</span> ve solo.
          </h1>
          <p className="font-sans-body mt-6 text-lg max-w-3xl" style={{ color: "var(--ink-soft)", lineHeight: 1.6 }}>
            Trabajamos en red con colegios, psicólogos, fisios, osteópatas, dentistas, podólogos y terapeutas ocupacionales. Muchos pacientes que pasan por tu consulta tienen un problema visual funcional sin diagnosticar — trabajando juntos llegamos antes y llegamos mejor.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="max-w-5xl mx-auto px-6" style={{ marginBottom: "4rem" }}>
        <div className="grid md:grid-cols-3 gap-5">
          {STATS.map((s) => (
            <div key={s.value} className="soft-card" style={{ textAlign: "center", padding: "2rem 1.5rem" }}>
              <div className="handwritten" style={{ fontSize: "3rem", color: "var(--sage-600)", lineHeight: 1 }}>{s.value}</div>
              <div className="font-sans-body text-sm mt-3" style={{ color: "var(--ink-soft)", lineHeight: 1.5 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Sectors grid */}
      <section className="section" style={{ paddingTop: "0" }}>
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="section-eyebrow">¿Eres uno de estos profesionales?</span>
            <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.7rem, 3vw, 2.4rem)" }}>Casos en los que <span className="handwritten">colaboramos</span></h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {SECTORS.map((s) => {
              const Icon = s.icon;
              const isActive = activeSlug === s.slug;
              return (
                <article
                  key={s.slug}
                  className="soft-card"
                  data-testid={`sector-card-${s.slug}`}
                  style={{ borderTop: isActive ? "3px solid var(--sage-600)" : "3px solid transparent", transition: "border-color 0.2s" }}
                >
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0 rounded-2xl flex items-center justify-center" style={{ width: 56, height: 56, background: "var(--sage-50)", border: "1px solid var(--sage-100)" }}>
                      <Icon size={26} style={{ color: "var(--sage-600)" }} />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-xl font-medium" style={{ color: "var(--ink)" }}>{s.title}</h3>
                      <p className="font-sans-body text-sm mt-2" style={{ color: "var(--ink-soft)", lineHeight: 1.55 }}>{s.short}</p>
                    </div>
                  </div>

                  <div className="mt-5">
                    <div className="text-xs uppercase tracking-[0.18em] mb-2" style={{ color: "var(--sage-600)", fontWeight: 600 }}>Casos típicos</div>
                    <ul className="font-sans-body text-sm space-y-1.5" style={{ color: "var(--ink)", listStyle: "none", padding: 0 }}>
                      {s.cases.map((c) => (
                        <li key={c} style={{ paddingLeft: "1rem", position: "relative" }}>
                          <span style={{ position: "absolute", left: 0, color: "var(--sage-500)" }}>·</span>{c}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="font-sans-body text-sm mt-4 p-3 rounded-xl" style={{ color: "var(--ink-soft)", background: "var(--cream)", borderLeft: "3px solid var(--sage-300, #b6c594)", lineHeight: 1.55 }}>
                    {s.pitch}
                  </p>

                  <button
                    className="btn-primary mt-5 w-full"
                    onClick={() => openForm(s.slug)}
                    data-testid={`sector-derivar-${s.slug}`}
                  >
                    Derivar un paciente <ArrowRight size={16} />
                  </button>
                </article>
              );
            })}
          </div>
        </div>
      </section>

      {/* Referral form */}
      <section className="section" id="derivacion" style={{ background: "var(--sage-50)" }}>
        <div className="max-w-3xl mx-auto px-6">
          <span className="section-eyebrow">Formulario de derivación</span>
          <h2 className="section-title mt-3" style={{ fontSize: "clamp(1.7rem, 3vw, 2.4rem)" }}>Derívanos un <span className="handwritten">paciente</span></h2>
          <p className="font-sans-body mt-4" style={{ color: "var(--ink-soft)", maxWidth: 640 }}>
            Sin listas de espera. Te respondemos en 24-48 h y, tras la evaluación, te enviamos un informe detallado para coordinar el tratamiento. La colaboración es bidireccional — también te derivaremos pacientes cuando se beneficien de tu especialidad.
          </p>

          <form onSubmit={submit} className="soft-card mt-8" data-testid="referral-form">
            {/* Honeypot anti-spam — invisible para humanos, los bots lo rellenan */}
            <input
              type="text"
              name="website"
              tabIndex="-1"
              autoComplete="off"
              value={form.website}
              onChange={(e) => setForm({ ...form, website: e.target.value })}
              style={{ position: "absolute", left: "-9999px", opacity: 0, pointerEvents: "none", height: 0, width: 0 }}
              aria-hidden="true"
            />
            <div className="grid md:grid-cols-2 gap-5">
              <div className="md:col-span-2">
                <label className="field-label">Tu profesión <span style={{ color: "var(--terracotta)" }}>*</span></label>
                <select
                  required
                  value={form.professional_role}
                  onChange={(e) => setForm({ ...form, professional_role: e.target.value })}
                  className="field-input"
                  data-testid="referral-role"
                >
                  <option value="">Selecciona...</option>
                  {SECTORS.map((s) => (<option key={s.slug} value={s.slug}>{s.title}</option>))}
                  <option value="otros">Otra profesión</option>
                </select>
              </div>
              <div>
                <label className="field-label">Tu nombre <span style={{ color: "var(--terracotta)" }}>*</span></label>
                <input required value={form.professional_name} onChange={(e) => setForm({ ...form, professional_name: e.target.value })} className="field-input" data-testid="referral-name" />
              </div>
              <div>
                <label className="field-label">Centro o consulta</label>
                <input value={form.professional_center} onChange={(e) => setForm({ ...form, professional_center: e.target.value })} className="field-input" data-testid="referral-center" />
              </div>
              <div>
                <label className="field-label">Email <span style={{ color: "var(--terracotta)" }}>*</span></label>
                <input required type="email" value={form.professional_email} onChange={(e) => setForm({ ...form, professional_email: e.target.value })} className="field-input" data-testid="referral-email" />
              </div>
              <div>
                <label className="field-label">Teléfono</label>
                <input value={form.professional_phone} onChange={(e) => setForm({ ...form, professional_phone: e.target.value })} className="field-input" data-testid="referral-phone" />
              </div>

              <div className="md:col-span-2 mt-2 pt-4" style={{ borderTop: "1px dashed var(--sage-200)" }}>
                <div className="text-xs uppercase tracking-[0.18em] mb-1" style={{ color: "var(--sage-600)", fontWeight: 600 }}>Paciente derivado (opcional)</div>
                <p className="font-sans-body text-xs" style={{ color: "var(--ink-soft)" }}>Comparte sólo lo que el paciente te haya autorizado. También puedes pedir que te llamemos primero a ti.</p>
              </div>
              <div>
                <label className="field-label">Nombre del paciente</label>
                <input value={form.patient_name} onChange={(e) => setForm({ ...form, patient_name: e.target.value })} className="field-input" data-testid="referral-patient-name" />
              </div>
              <div>
                <label className="field-label">Edad</label>
                <input value={form.patient_age} onChange={(e) => setForm({ ...form, patient_age: e.target.value })} className="field-input" data-testid="referral-patient-age" />
              </div>
              <div className="md:col-span-2">
                <label className="field-label">Teléfono del paciente</label>
                <input value={form.patient_phone} onChange={(e) => setForm({ ...form, patient_phone: e.target.value })} className="field-input" data-testid="referral-patient-phone" />
              </div>

              <div className="md:col-span-2">
                <label className="field-label">Motivo de la derivación <span style={{ color: "var(--terracotta)" }}>*</span></label>
                <textarea required rows="4" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} className="field-textarea" data-testid="referral-reason" placeholder="Qué sospechas, síntomas observados, diagnósticos previos, urgencia, etc."></textarea>
              </div>

              <div className="md:col-span-2">
                <label className="flex items-start gap-3 cursor-pointer rounded-xl p-3" style={{ background: form.consent_data_protection ? "var(--sage-50)" : "var(--cream)", border: `1px solid ${form.consent_data_protection ? "var(--sage-300, #b6c594)" : "var(--sage-100)"}`, transition: "all 0.2s" }}>
                  <input
                    type="checkbox"
                    required
                    checked={form.consent_data_protection}
                    onChange={(e) => setForm({ ...form, consent_data_protection: e.target.checked })}
                    style={{ accentColor: "var(--sage-500)", marginTop: "3px", width: 18, height: 18 }}
                    data-testid="referral-consent"
                  />
                  <span className="font-sans-body text-sm" style={{ color: "var(--ink)" }}>
                    He leído y acepto la <Link to="/legal" target="_blank" className="underline" style={{ color: "var(--sage-600)" }}>política de protección de datos</Link> y confirmo que dispongo del consentimiento del paciente para compartir sus datos. <span style={{ color: "var(--terracotta)" }}>*</span>
                  </span>
                </label>
              </div>
            </div>

            <button type="submit" disabled={sending} className="btn-primary mt-6 w-full" data-testid="referral-submit">
              <Send size={16} /> {sending ? "Enviando..." : "Enviar derivación"}
            </button>
          </form>

          <p className="font-sans-body text-sm text-center mt-8" style={{ color: "var(--ink-soft)" }}>
            ¿Prefieres llamar? <strong style={{ color: "var(--ink)" }}>91 404 48 77</strong> · <a href="mailto:saludyvision@saludyvision.es" style={{ color: "var(--sage-600)" }}>saludyvision@saludyvision.es</a>
          </p>
        </div>
      </section>
    </main>
  );
};

export default ProfessionalsPage;
